/**
 * Each page's view of its history over the selected range (ADR 0109): the range control in the
 * top bar and the chart that page asks about. One per page, so the pages stay small.
 */
import { formatCompactNumber, formatDecimal, formatMoney } from '@orgus/ui';
import type { DateRange } from '@orgus/ui';
import type { ReactNode } from 'react';
import { useDateRange } from './data.ts';
import { HistoryChart } from './HistoryChart.tsx';
import { RangeControl } from './RangeControl.tsx';

const hours = (n: number) => `${formatDecimal(n, 1)} h`;

function useRanged(title: string): { range: DateRange; control: ReactNode } {
  const { range, setRange } = useDateRange();
  return { range, control: <RangeControl className="col-span-full" title={title} range={range} setRange={setRange} /> };
}

/** Missions: transitions (created, started, done, failed) and landings per bucket. */
export function MissionsHistory() {
  const { range, control } = useRanged('History');
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      {control}
      <HistoryChart className="xl:col-span-2" range={range} title="Mission changes" metrics={['missions']} groupBy="action" noun="mission changes" height={160} />
      <HistoryChart range={range} title="Landings" metrics={['landings']} groupBy="result" noun="landings" height={160} />
    </div>
  );
}

/** Machines: hours online per machine. */
export function MachinesHistory() {
  const { range, control } = useRanged('Online history');
  return (
    <div className="flex flex-col gap-4">
      {control}
      <HistoryChart range={range} title="Hours online, by machine" metrics={['machines.onlineMinutes']} groupBy="machine" scale={1 / 60} format={hours} noun="machine hours online" height={160} />
    </div>
  );
}

/** Capacity: the most machines online at once, and where sessions started. */
export function CapacityHistory() {
  const { range, control } = useRanged('History');
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {control}
      <HistoryChart range={range} title="Machines online, at the peak" metrics={['machines.online']} noun="machines at the peak" height={160} />
      <HistoryChart range={range} title="Sessions started, by machine" metrics={['sessions.started']} groupBy="machine" noun="sessions started" height={160} />
    </div>
  );
}

/** Spend overview: spend by model and tokens over the range. */
export function SpendHistory() {
  const { range, control } = useRanged('Over time');
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      {control}
      <HistoryChart className="xl:col-span-2" range={range} title="Spend over time, by model" metrics={['cost.usd']} groupBy="model" format={formatMoney} noun="spent" height={180} />
      <HistoryChart range={range} title="Tokens" metrics={['tokens.input', 'tokens.output']} format={formatCompactNumber} noun="tokens" height={180} />
    </div>
  );
}

/** Audit: entries per bucket by family, for the explorer's range. */
export function AuditHistory({ range }: { range: DateRange }) {
  return <HistoryChart range={range} title="Audit entries" metrics={['audit']} groupBy="action" noun="audit entries" height={140} />;
}

